// @flow
import { useMemo } from 'react';
import { useMutation, MutationOptions } from 'react-query';
import { postCreateAffordaPortalHousehold } from 'services/householdService';
import { useAppContext } from 'contexts/AppContext';
import {
  isMinor as checkIsMinor,
  getHeadOfHousehold,
} from 'pages/AffordaHousehold/utils';
import STUDENT_STATUS from 'constants/studentStatus';
import { v4 as uuidv4 } from 'uuid';
import HOUSEHOLD_DESIGNATION_CODES from 'constants/householdDesignationCodes';
import AFFORDABLE_RELATIONSHIPS from 'constants/affordableRelationships';
import APPLICANT_TYPES from 'constants/applicantTypes';
import { useHouseholdMembersContext } from 'contexts/HouseholdMembersContext';
import { getPrimaryOrSecondaryForm } from './useCreateHouseholdMemberMutation.utils';
import { useUserContext } from 'contexts/UserContext';

const unbornChildTemplate = {
  applicantType: APPLICANT_TYPES.MINOR,
  firstName: 'Unborn Child',
  lastName: '',
  birthday: '2016-01-01',
  householdDesignation: HOUSEHOLD_DESIGNATION_CODES.UNBORN_CHILD,
  legallyResponsible: [],
  affordableRelationship: AFFORDABLE_RELATIONSHIPS.UNBORN_CHILD,
  id: '93ea78e4-080c-4d36-ae3b-c86e3e71730d',
  isFosterChild: 'no',
  jointCustody: 'no',
  relationship: AFFORDABLE_RELATIONSHIPS.CHILD,
  studentStatus: STUDENT_STATUS.NOT_A_STUDENT?.value,
  type: 'minors',
};
type Props = {
  householdForm: Object,
  options: MutationOptions,
};
const useCreateAffordaPortalHouseholdMutation = ({
  householdForm,
  options,
}: Props = {}) => {
  const {
    organizationId,
    propertyId,
    relationships,
    affordableRelationships,
    applicantTypes,
    marketingInfo: { propertyClass },
  } = useAppContext();
  const { portalUserId } = useUserContext();
  const { refetchHouseholdMembers, refetchUserProfile } =
    useHouseholdMembersContext();

  const members = useMemo(() => {
    if (!householdForm?.members?.length) return [];

    // Add unborn children to the members
    const unbornChildren = [];
    if (householdForm?.additionalMinors?.pregnancyCount) {
      const pregnancyCount = householdForm?.additionalMinors?.pregnancyCount;

      const headOfHousehold = getHeadOfHousehold(householdForm.members);

      for (let i = 0; i < pregnancyCount; i++) {
        unbornChildren.push({
          ...unbornChildTemplate,
          id: uuidv4(),
          firstName: `Unborn`,
          lastName: `Child ${i + 1}`,
          birthday: householdForm?.additionalMinors?.pregnancyDueDate,
          legallyResponsible: [headOfHousehold?.id ?? ''],
        });
      }
    }

    // Get the members from the household form
    const householdMembers = householdForm?.members?.map((member) => {
      const isMinor = checkIsMinor(member);

      if (isMinor) return member;

      const memberForm = getPrimaryOrSecondaryForm({
        additionalMinors: householdForm?.additionalMinors,
        affordableRelationships,
        applicantTypes,
        member,
        members: [...householdForm?.members, ...unbornChildren],
        relationships,
        studentStatus: householdForm?.studentStatus,
      });

      return {
        ...member,
        formProgress: memberForm.formPayload,
      };
    });

    // return the new members
    return [...householdMembers, ...unbornChildren];
  }, [householdForm, affordableRelationships, applicantTypes, relationships]);

  const hasRequiredParams = Boolean(
    organizationId &&
      propertyId &&
      portalUserId &&
      propertyClass &&
      members?.length,
  );

  const createAffordaPortalHouseholdMutation = useMutation(
    async () => {
      try {
        return await postCreateAffordaPortalHousehold({
          organizationId,
          propertyId,
          portalUserId,
          householdType: propertyClass, // householdType,
          data: {
            members,
          },
          options,
        });
      } catch (error) {
        console.error('Error creating Afforda portal household:', error);
        throw error;
      }
    },
    {
      ...options,
      onSuccess: async (response) => {
        await refetchUserProfile();
        await refetchHouseholdMembers();
        options?.onSuccess(response);
        return response;
      },
      enabled: options?.enabled
        ? options.enabled && hasRequiredParams
        : hasRequiredParams,
    },
  );

  return {
    ...createAffordaPortalHouseholdMutation,
    isLoading: createAffordaPortalHouseholdMutation.isLoading,
  };
};

export default useCreateAffordaPortalHouseholdMutation;
